<template>
    <div class="w-full">
        <div
            class="w-full"
            :class="
                !clean && (form.padlocks.length || form.rimlocks.length) && !form.keys.customColors ? 'xl:mb-24' : ''
            "
        >
            <!-- PADLOCKS -->
            <transition-group name="pop">
                <div class="pt-4" v-if="form.padlocks" v-for="(option, index) in form.padlocks" :key="index">
                    <div
                        class="flex flex-wrap bg-white border border-dgreen py-4 px-6 rounded-lg relative items-center"
                    >
                        <div class="w-full md:w-1/4 pb-4 md:pb-0">
                            <!-- Image -->
                            <vue-picture
                                :image="option.image"
                                :style="'rounded mx-auto w-32'"
                                :height="128"
                                :width="128"
                            />
                        </div>

                        <div class="w-full md:w-3/4 md:pl-4">
                            <div class="space-y-1 text-sm md:text-base">
                                <div class="mb-4">
                                    <strong>{{ option.value }}</strong>

                                    <a
                                        :href="url('product/' + option.slug)"
                                        target="_blank"
                                        class="ml-3 text-blue-600 no-underline text-sm"
                                        >Bekijk product
                                        <fa :icon="['fal', 'external-link']" class="h-2.5 w-2.5 ml-1 mb-0.5"
                                    /></a>
                                </div>
                                <div>
                                    <fa :icon="['fal', 'check']" class="text-dgreen mr-2 w-4" /> Inclusief cilinder
                                </div>
                                <div>
                                    <fa :icon="['fal', 'check']" class="text-dgreen mr-2 w-4" /> Gelijksluitend op
                                    onderstaande set
                                </div>

                                <div class="font-extrabold text-lg mt-3 md:absolute right-4 bottom-4">
                                    {{ price(option.price) }}
                                </div>
                            </div>
                        </div>

                        <!-- Right buttons -->
                        <div class="absolute right-4 top-4 items-center justify-end">
                            <div
                                class="cursor-pointer p-1 text-gray-500 hover:text-gray-800 group"
                                @click="removePadlock"
                            >
                                <fa :icon="['fal', 'trash-alt']" class="h-5 w-5 transition" />
                                <span class="tippy">Verwijder hangslot</span>
                            </div>
                        </div>
                    </div>
                </div>
            </transition-group>

            <!-- RIMLOCKS -->
            <transition-group name="pop">
                <div class="pt-4" v-for="(option, index) in form.rimlocks" :key="index" v-if="form.padlocks">
                    <div
                        class="flex flex-wrap bg-white border border-dgreen py-4 px-6 rounded-lg relative items-center"
                    >
                        <div class="w-full md:w-1/4 pb-4 md:pb-0">
                            <!-- Image -->
                            <vue-picture
                                :image="option.image"
                                :style="'rounded mx-auto w-48'"
                                :height="192"
                                :width="192"
                            />
                        </div>

                        <div class="w-full md:w-3/4 md:pl-4">
                            <div class="space-y-1 mb-6 text-sm md:text-base">
                                <div class="mb-4">
                                    <strong>{{ option.value }}</strong>

                                    <a
                                        :href="'/product/' + option.slug"
                                        target="_blank"
                                        class="md:ml-3 text-blue-600 no-underline text-sm block md:inline-block"
                                        >Bekijk product
                                        <fa :icon="['fal', 'external-link']" class="h-2.5 w-2.5 ml-1 mb-0.5"
                                    /></a>
                                </div>
                                <div>
                                    <fa :icon="['fal', 'check']" class="text-dgreen mr-2 w-4" /> Inclusief
                                    staartcilinder
                                </div>
                                <div>
                                    <fa :icon="['fal', 'check']" class="text-dgreen mr-2 w-4" /> Gelijksluitend op
                                    onderstaande set
                                </div>
                            </div>
                            <div class="w-full flex flex-wrap">
                                <div class="w-full md:pr-8">
                                    <div class="w-full mb-2 mt-3 lg:mt-0">
                                        <info-modal title="Draairichting" :body="rotationDescription">
                                            <strong class="inline-block">Draairichting</strong>
                                        </info-modal>
                                    </div>

                                    <div class="flex -mx-2 mt-2">
                                        <div
                                            class="rounded-md hover:bg-gray-50 border cursor-pointer mx-2 mb-2 relative items-center"
                                            v-for="value in [
                                                { name: 'Links', id: 1 },
                                                { name: 'Rechts', id: 2 },
                                            ]"
                                            :class="{
                                                'bg-dgreen2/5 border-dgreen2': value.id == option.rotation,
                                            }"
                                            :key="value.id"
                                            @click="option.rotation = value.id"
                                        >
                                            <span class="inline-block p-4">{{ value.name }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="w-full">
                                    <div class="w-full mb-2 mt-3 lg:mt-0">
                                        <info-modal
                                            title="Type rozet"
                                            :body="'U kunt hier kiezen om uw oplegslot extra te beschermen met een kerntrekrozet'"
                                        >
                                            <strong class="inline-block">Type rozet</strong>
                                        </info-modal>
                                    </div>

                                    <div class="flex -mx-2 mt-2">
                                        <div
                                            class="rounded-md hover:bg-gray-50 border cursor-pointer mx-2 mb-2 relative items-center"
                                            v-for="value in [
                                                { name: 'Standaard rozet', id: 1 },
                                                { name: 'Kerntrekrozet RVS + 39,50', id: 2 },
                                            ]"
                                            :class="{
                                                'bg-dgreen2/5 border-dgreen2': value.id == option.rozet,
                                            }"
                                            :key="value.id"
                                            @click="option.rozet = value.id"
                                        >
                                            <span class="inline-block p-4">{{ value.name }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="font-extrabold text-lg mt-3 md:absolute right-4 md:bottom-4">
                                {{ price(parseFloat(option.price) + (option.rozet == 2 ? 39.5 : 0)) }}
                            </div>
                        </div>

                        <!-- Right buttons -->
                        <div class="absolute right-4 top-4 items-center justify-end">
                            <div
                                class="cursor-pointer p-1 text-gray-500 hover:text-gray-800 group"
                                @click="removeRimlock"
                            >
                                <fa :icon="['fal', 'trash-alt']" class="h-5 w-5 transition" />
                                <span class="tippy">Verwijder oplegslot</span>
                            </div>
                        </div>
                    </div>
                </div>
            </transition-group>
        </div>
        <AddLocksClean
            v-if="clean"
            :smartlocks="smartlocks"
            :product="product"
            @addPadlock="addPadlock"
            @addRimlock="addRimlock"
            @showModalSmartlock="showModalSmartlock"
        />
        <div class="w-full xl:w-1/2" v-else>
            <AddLocks
                :smartlocks="smartlocks"
                :product="product"
                @addPadlock="addPadlock"
                @addRimlock="addRimlock"
                @showModalSmartlock="showModalSmartlock"
            />
        </div>
    </div>
</template>

<script setup>
let emit = defineEmits(['showModalSmartlock']);

import AddLocks from '@/Components/Configuration/Legacy/AddLocks.vue';
import AddLocksClean from '@/Components/Configuration/Legacy/AddLocksClean.vue';

const props = defineProps({
    product: Object,
    form: Object,
    options: Object,
    clean: Boolean,
    smartlocks: Boolean,
});

const rotationDescription =
    '<div class="text-gray-800"><p>Als je bij een dichte deur het scharnier ziet zitten dan draait de deur altijd naar je toe. Zit het scharnier rechts? Dan is het een linksdraaiende deur. Zitten de scharnieren links? Dan spreken we over een rechtsdraaiende deur.</p><p><img alt="" src="https://files.doorax.nl/images/other/Draairichting-deur-Doorax.jpg" style="height:175px; width:726px"></p></div>';

function showModalSmartlock() {
    emit('showModalSmartlock', false);
}

function addPadlock() {
    if (!props.form.padlocks.length) {
        props.form.padlocks = [props.product.padlocks[0]];
    } else {
        props.form.padlocks.push(props.product.padlocks[0]);
    }
}

function addRimlock() {
    if (!props.form.rimlocks.length) {
        props.form.rimlocks = [props.product.rimlocks[0]];
    } else {
        props.form.rimlocks.push(props.product.rimlocks[0]);
    }
}

function removePadlock() {
    props.form.padlocks.splice(0, 1);
}

function removeRimlock() {
    props.form.rimlocks.splice(0, 1);
}
</script>
